import React, { useCallback, useEffect, useState } from "react";
import {
	Badge,
	Button,
	Card,
	CardBody,
	Col,
	Container,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Table
} from "reactstrap";
import { useSelector } from "react-redux";
import { FaArrowLeft, FaExternalLinkAlt, FaPrint } from "react-icons/fa";
import * as api from "services/api";
import dayjs from "dayjs";
import ls from "Localization";
import { toast } from "react-toastify";
import phoneNumber from "helpers/phoneNumber";
import { STATUS_LABEL, STATUS_COLOR } from "../constants";

const SOURCE_LABEL = {
	cron: "Automático",
	manual: "Manual",
	backfill: "Backfill"
};

function StatusBadge({ status }) {
	return (
		<Badge className="ml-2" color={STATUS_COLOR[status] || "danger"}>
			{STATUS_LABEL[status] || status}
		</Badge>
	);
}

function SentBadge({ sent }) {
	if (sent === null || sent === undefined) {
		return <Badge color="secondary">--</Badge>;
	}

	return sent ? (
		<Badge color="success">SIM</Badge>
	) : (
		<Badge color="danger">NÃO</Badge>
	);
}

function ChannelStatusBadge({ status }) {
	if (!status || !status.attempted) {
		return <Badge color="secondary">Não enviado</Badge>;
	}

	return status.success ? (
		<Badge color="success">Sucesso</Badge>
	) : (
		<Badge color="danger">Falha</Badge>
	);
}

function RecipientsTable({ recipients }) {
	if (!recipients || !recipients.length) {
		return <p className="text-muted">Nenhum destinatário encontrado.</p>;
	}

	return (
		<Table responsive size="sm" className="mb-0">
			<thead>
				<tr>
					<th>Nome</th>
					<th>E-mail</th>
					<th>Recebeu E-mail</th>
					<th>Telefone</th>
					<th>Recebeu WhatsApp</th>
				</tr>
			</thead>
			<tbody>
				{recipients.map((recipient, index) => (
					<tr key={recipient.userId || index}>
						<td>{recipient.name || "--"}</td>
						<td>{recipient.email || "--"}</td>
						<td>
							{recipient.email ? (
								<SentBadge sent={recipient.emailSent} />
							) : (
								"--"
							)}
						</td>
						<td>
							{recipient.phoneNumber
								? phoneNumber.format(recipient.phoneNumber)
								: "--"}
						</td>
						<td>
							{recipient.phoneNumber ? (
								<SentBadge sent={recipient.whatsappSent} />
							) : (
								"--"
							)}
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	);
}

function ProvidersRecipientsTable({ providers }) {
	if (!providers || !providers.length) {
		return <p className="text-muted">Nenhum prestador vinculado.</p>;
	}

	return (
		<Table responsive size="sm" className="mb-0">
			<thead>
				<tr>
					<th>Empresa Prestadora</th>
					<th>Status</th>
					<th>Nome</th>
					<th>E-mail</th>
					<th>Recebeu E-mail</th>
					<th>Telefone</th>
					<th>Recebeu WhatsApp</th>
				</tr>
			</thead>
			<tbody>
				{providers.map((provider, providerIndex) =>
					provider.recipients.length ? (
						provider.recipients.map((recipient, recipientIndex) => (
							<tr
								key={`${providerIndex}-${recipient.userId || recipientIndex}`}
							>
								{recipientIndex === 0 && (
									<>
										<td rowSpan={provider.recipients.length}>
											{provider.businessName}
										</td>
										<td rowSpan={provider.recipients.length}>
											<StatusBadge status={provider.status} />
										</td>
									</>
								)}
								<td>{recipient.name || "--"}</td>
								<td>{recipient.email || "--"}</td>
								<td>
									{recipient.email ? (
										<SentBadge sent={recipient.emailSent} />
									) : (
										"--"
									)}
								</td>
								<td>
									{recipient.phoneNumber
										? phoneNumber.format(recipient.phoneNumber)
										: "--"}
								</td>
								<td>
									{recipient.phoneNumber ? (
										<SentBadge sent={recipient.whatsappSent} />
									) : (
										"--"
									)}
								</td>
							</tr>
						))
					) : (
						<tr key={providerIndex}>
							<td>{provider.businessName}</td>
							<td>
								<StatusBadge status={provider.status} />
							</td>
							<td>--</td>
							<td>--</td>
							<td>--</td>
							<td>--</td>
							<td>--</td>
						</tr>
					)
				)}
			</tbody>
		</Table>
	);
}

function rowKey(row) {
	return `${row.reportType}:${row.logId}`;
}

function isChannelFailed(status) {
	return !!status?.attempted && !status?.success;
}

function isChannelEligible(status, forced) {
	if (!status?.attempted) return false;
	if (status.success) return !!forced;
	return true;
}

function buildResendRows(details) {
	const rows = [];

	if (details?._id) {
		rows.push({
			reportType: "contratante",
			logId: details._id,
			label: details.businessName,
			email: details.email,
			whatsapp: details.whatsapp,
			recipients: details.admins || []
		});
	}

	(details?.providers || [])
		.filter(provider => provider.logId)
		.forEach(provider => {
			rows.push({
				reportType: "prestador",
				logId: provider.logId,
				label: provider.businessName,
				email: provider.email,
				whatsapp: provider.whatsapp,
				recipients: provider.recipients || []
			});
		});

	return rows;
}

function ResendChannelHeader({ label, checked, onToggleColumn }) {
	if (!onToggleColumn) return label;

	return (
		<div className="d-flex align-items-center" style={{ gap: 6 }}>
			<input
				type="checkbox"
				checked={checked}
				onChange={onToggleColumn}
				title={`Marcar todos os itens de ${label} com falha`}
			/>
			{label}
		</div>
	);
}

function ResendChannelCell({ status, checked, eligible, onToggle }) {
	return (
		<div className="d-flex align-items-center" style={{ gap: 6 }}>
			<input
				type="checkbox"
				checked={checked}
				disabled={!eligible}
				onChange={onToggle}
			/>
			<ChannelStatusBadge status={status} />
		</div>
	);
}

function AdminsResendTable({
	row,
	selection,
	forceRows,
	onToggleCell,
	onToggleForce
}) {
	if (!row) {
		return (
			<p className="text-muted">
				O relatório do contratante ainda não foi processado neste período.
			</p>
		);
	}

	const key = rowKey(row);
	const forced = !!forceRows[key];
	const recipients = row.recipients?.length ? row.recipients : [null];

	return (
		<Table responsive size="sm" className="mb-0">
			<thead>
				<tr>
					<th>Nome</th>
					<th>E-mail</th>
					<th>Telefone</th>
					<th>E-mail</th>
					<th>WhatsApp</th>
					<th>Forçar reenvio</th>
				</tr>
			</thead>
			<tbody>
				{recipients.map((recipient, index) => (
					<tr key={index}>
						<td>{recipient?.name || "--"}</td>
						<td>{recipient?.email || "--"}</td>
						<td>
							{recipient?.phoneNumber
								? phoneNumber.format(recipient.phoneNumber)
								: "--"}
						</td>
						{index === 0 && (
							<>
								<td rowSpan={recipients.length}>
									<ResendChannelCell
										status={row.email}
										checked={!!selection[`${key}:email`]}
										eligible={isChannelEligible(row.email, forced)}
										onToggle={() => onToggleCell(row, "email")}
									/>
								</td>
								<td rowSpan={recipients.length}>
									<ResendChannelCell
										status={row.whatsapp}
										checked={!!selection[`${key}:whatsapp`]}
										eligible={isChannelEligible(row.whatsapp, forced)}
										onToggle={() => onToggleCell(row, "whatsapp")}
									/>
								</td>
								<td rowSpan={recipients.length}>
									<input
										type="checkbox"
										checked={forced}
										onChange={() => onToggleForce(row)}
										title="Permite selecionar um canal que já teve sucesso"
									/>
								</td>
							</>
						)}
					</tr>
				))}
			</tbody>
		</Table>
	);
}

function ProvidersResendTable({
	rows,
	selection,
	forceRows,
	onToggleCell,
	onToggleForce,
	onToggleColumn
}) {
	if (!rows.length) {
		return (
			<p className="text-muted">
				Nenhum prestador com relatório processado neste período.
			</p>
		);
	}

	const columnChecked = channel => {
		const eligible = rows.filter(row => isChannelFailed(row[channel]));
		return (
			eligible.length > 0 &&
			eligible.every(row => selection[`${rowKey(row)}:${channel}`])
		);
	};

	return (
		<Table responsive size="sm" className="mb-0">
			<thead>
				<tr>
					<th>Empresa Prestadora</th>
					<th>Nome</th>
					<th>E-mail</th>
					<th>Telefone</th>
					<th>
						<ResendChannelHeader
							label="E-mail"
							checked={columnChecked("email")}
							onToggleColumn={() => onToggleColumn("email")}
						/>
					</th>
					<th>
						<ResendChannelHeader
							label="WhatsApp"
							checked={columnChecked("whatsapp")}
							onToggleColumn={() => onToggleColumn("whatsapp")}
						/>
					</th>
					<th>Forçar reenvio</th>
				</tr>
			</thead>
			<tbody>
				{rows.map(row => {
					const key = rowKey(row);
					const forced = !!forceRows[key];
					const recipients = row.recipients?.length ? row.recipients : [null];

					return recipients.map((recipient, index) => (
						<tr key={`${key}-${index}`}>
							{index === 0 && <td rowSpan={recipients.length}>{row.label}</td>}
							<td>{recipient?.name || "--"}</td>
							<td>{recipient?.email || "--"}</td>
							<td>
								{recipient?.phoneNumber
									? phoneNumber.format(recipient.phoneNumber)
									: "--"}
							</td>
							{index === 0 && (
								<>
									<td rowSpan={recipients.length}>
										<ResendChannelCell
											status={row.email}
											checked={!!selection[`${key}:email`]}
											eligible={isChannelEligible(row.email, forced)}
											onToggle={() => onToggleCell(row, "email")}
										/>
									</td>
									<td rowSpan={recipients.length}>
										<ResendChannelCell
											status={row.whatsapp}
											checked={!!selection[`${key}:whatsapp`]}
											eligible={isChannelEligible(row.whatsapp, forced)}
											onToggle={() => onToggleCell(row, "whatsapp")}
										/>
									</td>
									<td rowSpan={recipients.length}>
										<input
											type="checkbox"
											checked={forced}
											onChange={() => onToggleForce(row)}
											title="Permite selecionar um canal que já teve sucesso"
										/>
									</td>
								</>
							)}
						</tr>
					));
				})}
			</tbody>
		</Table>
	);
}

function ResendResultsList({ results, rows }) {
	if (!results || !results.length) return null;

	const labelFor = result =>
		rows.find(
			row => row.reportType === result.reportType && row.logId === result.logId
		)?.label || result.logId;

	return (
		<ul className="mt-3 mb-0">
			{results.map((result, index) => (
				<li key={index}>
					{result.reportType === "contratante" ? "Contratante" : "Prestador"} —{" "}
					{labelFor(result)} —{" "}
					{result.channel === "email" ? "E-mail" : "WhatsApp"}:{" "}
					{result.skipped ? (
						<span className="text-muted">pulado ({result.skipReason})</span>
					) : result.success ? (
						<span className="text-success">reenviado com sucesso</span>
					) : (
						<span className="text-danger">falhou ({result.error})</span>
					)}
				</li>
			))}
		</ul>
	);
}

function MonthlyDetails({ match, history }) {
	const { id } = match.params;
	const { token } = useSelector(s => s.login);

	const [details, setDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const [resendModalOpen, setResendModalOpen] = useState(false);
	const [selection, setSelection] = useState({});
	const [forceRows, setForceRows] = useState({});
	const [resending, setResending] = useState(false);
	const [resendResults, setResendResults] = useState(null);
	const [triggering, setTriggering] = useState(false);
	const [triggerModalOpen, setTriggerModalOpen] = useState(false);
	const [triggerTypes, setTriggerTypes] = useState({
		contratante: false,
		prestador: false
	});

	const load = useCallback(async () => {
		setLoading(true);

		try {
			const response = await api.sendGet(
				`/monthly-companies-report/${id}/details`,
				{ Authorization: "Bearer " + token }
			);

			const result = await response.json();

			if (result.isValid) {
				setDetails(result.model);
				setError(null);
			} else {
				setError(
					(result.errors && result.errors.default) ||
						"Erro ao buscar detalhes do relatório"
				);
			}
		} catch (err) {
			setError("Erro de conexão");
		} finally {
			setLoading(false);
		}
	}, [id, token]);

	useEffect(() => {
		load();
	}, [load]);

	const resendRows = buildResendRows(details);

	useEffect(() => {
		const next = {};

		resendRows.forEach(row => {
			["email", "whatsapp"].forEach(channel => {
				if (isChannelFailed(row[channel])) {
					next[`${rowKey(row)}:${channel}`] = true;
				}
			});
		});

		setSelection(next);
		setForceRows({});
		setResendResults(null);
	}, [details]);

	const toggleCell = (row, channel) => {
		const key = rowKey(row);

		if (!isChannelEligible(row[channel], forceRows[key])) return;

		setSelection(prev => ({
			...prev,
			[`${key}:${channel}`]: !prev[`${key}:${channel}`]
		}));
	};

	const toggleForce = row => {
		const key = rowKey(row);

		setForceRows(prev => ({ ...prev, [key]: !prev[key] }));
	};

	const toggleColumn = channel => {
		const eligibleRows = resendRows.filter(
			row => row.reportType === "prestador" && isChannelFailed(row[channel])
		);
		const allChecked =
			eligibleRows.length > 0 &&
			eligibleRows.every(row => selection[`${rowKey(row)}:${channel}`]);

		setSelection(prev => {
			const next = { ...prev };

			eligibleRows.forEach(row => {
				next[`${rowKey(row)}:${channel}`] = !allChecked;
			});

			return next;
		});
	};

	const handleResend = async () => {
		const items = [];

		resendRows.forEach(row => {
			["email", "whatsapp"].forEach(channel => {
				if (selection[`${rowKey(row)}:${channel}`]) {
					items.push({
						reportType: row.reportType,
						logId: row.logId,
						channel,
						force: !!forceRows[rowKey(row)]
					});
				}
			});
		});

		if (items.length === 0) {
			toast.warning("Selecione ao menos um item para reenviar");
			return;
		}

		setResending(true);
		setResendResults(null);

		try {
			const response = await api.sendPost(
				"/monthly-companies-report/resend",
				{ items },
				{ Authorization: "Bearer " + token }
			);

			const result = await response.json();

			if (result.isValid) {
				const { results } = result.model;
				const succeeded = results.filter(r => r.success && !r.skipped).length;
				const skipped = results.filter(r => r.skipped).length;
				const failed = results.filter(r => !r.success).length;

				setResendResults(results);

				if (failed === 0) {
					toast.success(
						`${succeeded} reenviado(s) com sucesso${
							skipped ? `, ${skipped} pulado(s)` : ""
						}`
					);
				} else {
					toast.error(`${failed} item(ns) falharam ao reenviar`);
				}

				await load();
			} else {
				toast.error("Erro ao reenviar");
			}
		} catch (err) {
			toast.error("Erro de conexão ao reenviar");
		} finally {
			setResending(false);
		}
	};

	const openTriggerModal = () => {
		setTriggerTypes({ contratante: false, prestador: false });
		setTriggerModalOpen(true);
	};

	const toggleTriggerModal = () => setTriggerModalOpen(prev => !prev);

	const toggleTriggerType = type =>
		setTriggerTypes(prev => ({ ...prev, [type]: !prev[type] }));

	const handleTriggerNow = async () => {
		const reportTypes = Object.keys(triggerTypes).filter(
			type => triggerTypes[type]
		);

		if (reportTypes.length === 0) return;

		setTriggering(true);

		try {
			const response = await api.sendPost(
				"/monthly-companies-report/trigger",
				{ companyIds: [details.companyId], reportTypes },
				{ Authorization: "Bearer " + token }
			);

			const result = await response.json();

			if (result.isValid) {
				const { period, scheduled, scheduledProviders } = result.model;
				const parts = [];

				if (scheduled > 0) parts.push("contratante");
				if (scheduledProviders > 0)
					parts.push(`${scheduledProviders} prestador(es)`);

				if (parts.length > 0) {
					toast.success(
						`Relatório de ${period} agendado: ${parts.join(" e ")}`
					);
				}

				if (triggerTypes.prestador && scheduledProviders === 0) {
					toast.warning("Nenhum prestador elegível vinculado");
				}

				setTriggerModalOpen(false);
				setTimeout(load, 3000);
			} else {
				toast.error(
					(result.errors && result.errors.default) || "Erro ao agendar disparo"
				);
			}
		} catch (err) {
			toast.error("Erro de conexão ao agendar disparo");
		} finally {
			setTriggering(false);
		}
	};

	const toggleResendModal = () => setResendModalOpen(prev => !prev);

	const backButton = (
		<Button
			outline
			className="no-print mb-3 mr-2"
			onClick={() => history.goBack()}
		>
			<FaArrowLeft style={{ marginRight: 8 }} />
			Voltar
		</Button>
	);

	const printButton = (
		<Button
			outline
			className="no-print mb-3  mr-2"
			onClick={() => window.print()}
		>
			<FaPrint style={{ marginRight: 8 }} />
			Imprimir
		</Button>
	);

	const resendButton = (
		<Button outline className="no-print mb-3" onClick={toggleResendModal}>
			Disparo Manual
		</Button>
	);

	if (loading) {
		return (
			<Container className="dashboard">
				{backButton}
				<p>Carregando...</p>
			</Container>
		);
	}

	if (error || !details) {
		return (
			<Container className="dashboard">
				{backButton}
				<p>{error || "Relatório não encontrado"}</p>
			</Container>
		);
	}

	const adminsCount = details.admins?.length || 0;
	const providersRecipientsCount = (details.providers || []).reduce(
		(total, provider) => total + (provider.recipients?.length || 0),
		0
	);
	const neverProcessed = !details._id;

	return (
		<Container className="dashboard">
			{backButton}
			{printButton}
			{resendButton}
			<Row>
				<Col md={12}>
					<Card>
						<CardBody>
							<div className="card__title d-flex align-center">
								<h4 className="bold-text">
									{details.businessName}
									{details.companyId && (
										<a
											href={`/companies/${details.companyId}`}
											target="_blank"
											rel="noopener noreferrer"
											className="ml-2"
											title="Abrir empresa em nova aba"
										>
											<FaExternalLinkAlt size={14} />
										</a>
									)}
									<StatusBadge status={details.status} />
								</h4>
							</div>

							<Row>
								<Col md={4}>
									<p>
										<strong>Processado em:</strong>{" "}
										{details.createdAt
											? dayjs(details.createdAt).format(
													ls.dateFormatWithoutSeconds
											  )
											: "-"}
									</p>
								</Col>
								<Col md={4}>
									<p>
										<strong>Período:</strong>{" "}
										{details.periodStart && details.periodEnd
											? `${dayjs(details.periodStart).format(
													ls.dateFormatShort
											  )} - ${dayjs(details.periodEnd).format(
													ls.dateFormatShort
											  )}`
											: "-"}
									</p>
								</Col>
								<Col md={4}>
									<p>
										<strong>Origem:</strong>{" "}
										{SOURCE_LABEL[details.source] || "-"}
									</p>
								</Col>
							</Row>

							<div className="d-flex align-items-center mt-2">
								{neverProcessed && (
									<p className="mb-0 mr-3">
										O relatório do contratante ainda não foi processado neste
										período.
									</p>
								)}
								<Button
									color="primary"
									className="mr-2"
									onClick={openTriggerModal}
									disabled={triggering}
								>
									{triggering ? "Agendando..." : "Disparar agora"}
								</Button>
							</div>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<Row>
				<Col md={12}>
					<Card>
						<CardBody>
							<div className="card__title">
								<h5 className="bold-text">
									Administradores da Empresa ({adminsCount})
								</h5>
							</div>
							<RecipientsTable recipients={details.admins} />
						</CardBody>
					</Card>
				</Col>
			</Row>

			<Row>
				<Col md={12}>
					<Card>
						<CardBody>
							<div className="card__title">
								<h5 className="bold-text">
									Prestadores ({providersRecipientsCount})
								</h5>
							</div>
							<ProvidersRecipientsTable providers={details.providers} />
						</CardBody>
					</Card>
				</Col>
			</Row>

			<Modal isOpen={resendModalOpen} toggle={toggleResendModal} size="xl">
				<ModalHeader
					toggle={toggleResendModal}
					className="card__title bold-text"
				>
					Disparo Manual
				</ModalHeader>
				<ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }}>
					<p className="text-muted">
						Só é possível selecionar canais que falharam. Um canal que já teve
						sucesso fica bloqueado, a menos que você marque &quot;Forçar
						reenvio&quot; na linha.
					</p>

					<h6 className="bold-text mt-3">Administradores da Empresa</h6>
					<AdminsResendTable
						row={resendRows.find(row => row.reportType === "contratante")}
						selection={selection}
						forceRows={forceRows}
						onToggleCell={toggleCell}
						onToggleForce={toggleForce}
					/>

					<h6 className="bold-text mt-4">Prestadores</h6>
					<ProvidersResendTable
						rows={resendRows.filter(row => row.reportType === "prestador")}
						selection={selection}
						forceRows={forceRows}
						onToggleCell={toggleCell}
						onToggleForce={toggleForce}
						onToggleColumn={toggleColumn}
					/>

					<ResendResultsList results={resendResults} rows={resendRows} />
				</ModalBody>
				<ModalFooter>
					<Button outline onClick={toggleResendModal}>
						Fechar
					</Button>
					<Button color="primary" onClick={handleResend} disabled={resending}>
						{resending ? "Reenviando..." : "Reenviar selecionados"}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal isOpen={triggerModalOpen} toggle={toggleTriggerModal}>
				<ModalHeader
					toggle={toggleTriggerModal}
					className="card__title bold-text"
				>
					Disparo Manual
				</ModalHeader>
				<ModalBody>
					<p className="text-muted">
						Selecione para quem o relatório do período de referência será
						agendado.
					</p>

					<label className="d-flex align-items-center" style={{ gap: 6 }}>
						<input
							type="checkbox"
							checked={triggerTypes.contratante}
							onChange={() => toggleTriggerType("contratante")}
						/>
						Contratante (administradores da empresa)
					</label>

					<label className="d-flex align-items-center" style={{ gap: 6 }}>
						<input
							type="checkbox"
							checked={triggerTypes.prestador}
							onChange={() => toggleTriggerType("prestador")}
						/>
						Prestadores vinculados ({(details.providers || []).length})
					</label>
				</ModalBody>
				<ModalFooter>
					<Button outline onClick={toggleTriggerModal}>
						Cancelar
					</Button>
					<Button
						color="primary"
						onClick={handleTriggerNow}
						disabled={
							triggering ||
							(!triggerTypes.contratante && !triggerTypes.prestador)
						}
					>
						{triggering ? "Agendando..." : "Disparar"}
					</Button>
				</ModalFooter>
			</Modal>
		</Container>
	);
}

export default MonthlyDetails;
